// ======================== CATEGORY MANAGER COMPONENT ========================
// Gestion des catégories POS (création, modification, suppression, réorganisation)
import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    Plus, 
    Edit2, 
    Trash2, 
    GripVertical,
    ArrowUp,
    ArrowDown,
    AlertTriangle,
    Check,
    X,
    FolderPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

// Emojis disponibles pour les catégories
const CATEGORY_EMOJIS = [
    '☕', '🍵', '🌿', '🥤', '🧃', '💧', '🍊', '🍽️', '🥪', '🍰', '🍕', '🍔',
    '🌮', '🍜', '🍝', '🥗', '🍗', '🥩', '🐟', '🍳', '🥐', '🧁', '🍺', '🍷'
];

const CategoryManager = ({ 
    api, 
    categories, 
    products,
    onCategoriesChange,
    isOpen,
    onClose 
}) => {
    const [editingCategory, setEditingCategory] = useState(null);
    const [deleteCategory, setDeleteCategory] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [moveToCategory, setMoveToCategory] = useState('');
    const [deleteAction, setDeleteAction] = useState('move'); // 'move' or 'delete'
    
    const [form, setForm] = useState({
        name: '',
        icon: '📦',
        order: 0
    });

    // Reset form when opening for new category
    const handleNewCategory = () => {
        setEditingCategory(null);
        setForm({
            name: '',
            icon: '📦',
            order: categories.length + 1
        });
        setShowEditDialog(true);
    };

    // Open edit dialog
    const handleEditCategory = (cat) => {
        setEditingCategory(cat);
        setForm({
            name: cat.name,
            icon: cat.icon || '📦',
            order: cat.order || 0
        });
        setShowEditDialog(true);
    };

    // Save category (create or update)
    const handleSaveCategory = async () => {
        if (!form.name.trim()) {
            toast.error('Le nom est requis');
            return;
        }

        try {
            if (editingCategory) {
                // Update
                await api().put(`/pos/categories/${editingCategory.id}`, {
                    name: form.name.trim(),
                    icon: form.icon,
                    order: form.order
                });
                toast.success('Catégorie modifiée');
            } else {
                // Create
                await api().post('/pos/categories', {
                    name: form.name.trim(),
                    icon: form.icon,
                    type: 'regular'
                });
                toast.success('Catégorie créée');
            }
            setShowEditDialog(false);
            onCategoriesChange();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
        }
    };

    // Initiate delete
    const handleDeleteClick = (cat) => {
        const productCount = products.filter(p => p.category === cat.id).length;
        setDeleteCategory({ ...cat, productCount });
        setMoveToCategory('');
        setDeleteAction(productCount > 0 ? 'move' : 'delete');
        setShowDeleteConfirm(true);
    };

    // Confirm delete
    const handleConfirmDelete = async () => {
        if (!deleteCategory) return;

        try {
            const params = new URLSearchParams();
            if (deleteAction === 'move' && moveToCategory) {
                params.append('move_to', moveToCategory);
            } else if (deleteAction === 'delete') {
                params.append('force', 'true');
            }

            await api().delete(`/pos/categories/${deleteCategory.id}?${params.toString()}`);
            toast.success('Catégorie supprimée');
            setShowDeleteConfirm(false);
            setDeleteCategory(null);
            onCategoriesChange();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    // Move category up/down
    const handleMoveCategory = async (cat, direction) => {
        const sortedCats = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
        const currentIndex = sortedCats.findIndex(c => c.id === cat.id);
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        if (newIndex < 0 || newIndex >= sortedCats.length) return;

        // Swap orders
        const newOrders = sortedCats.map((c, i) => {
            if (i === currentIndex) return { id: c.id, order: newIndex + 1 };
            if (i === newIndex) return { id: c.id, order: currentIndex + 1 };
            return { id: c.id, order: i + 1 };
        });

        try {
            await api().put('/pos/categories/reorder', newOrders);
            onCategoriesChange();
        } catch (error) {
            toast.error('Erreur lors du réordonnancement');
        }
    };

    // Sort alphabetically
    const handleSortAlphabetically = async () => {
        const sortedCats = [...categories].sort((a, b) => a.name.localeCompare(b.name));
        const newOrders = sortedCats.map((c, i) => ({ id: c.id, order: i + 1 }));

        try {
            await api().put('/pos/categories/reorder', newOrders);
            toast.success('Catégories triées par ordre alphabétique');
            onCategoriesChange();
        } catch (error) {
            toast.error('Erreur lors du tri');
        }
    };

    const sortedCategories = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));

    return (
        <>
            {/* Main Dialog */}
            <Dialog open={isOpen} onOpenChange={onClose}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderPlus className="w-5 h-5 text-primary" />
                            Gestion des catégories
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto">
                        {/* Actions */}
                        <div className="flex gap-2 mb-4">
                            <Button onClick={handleNewCategory} size="sm" className="bg-green-600 hover:bg-green-700">
                                <Plus className="w-4 h-4 mr-1" />
                                Nouvelle catégorie
                            </Button>
                            <Button onClick={handleSortAlphabetically} variant="outline" size="sm">
                                A → Z
                            </Button>
                        </div>

                        {/* Categories List */}
                        <div className="space-y-2">
                            {sortedCategories.map((cat, index) => {
                                const productCount = products.filter(p => p.category === cat.id).length;
                                return (
                                    <div 
                                        key={cat.id}
                                        className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border"
                                    >
                                        {/* Drag handle & order buttons */}
                                        <div className="flex flex-col gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => handleMoveCategory(cat, 'up')}
                                                disabled={index === 0}
                                            >
                                                <ArrowUp className="w-3 h-3" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => handleMoveCategory(cat, 'down')}
                                                disabled={index === sortedCategories.length - 1}
                                            >
                                                <ArrowDown className="w-3 h-3" />
                                            </Button>
                                        </div>

                                        {/* Icon */}
                                        <span className="text-2xl">{cat.icon || '📦'}</span>

                                        {/* Name & count */}
                                        <div className="flex-1">
                                            <p className="font-medium">{cat.name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {productCount} produit{productCount !== 1 ? 's' : ''}
                                            </p>
                                        </div>

                                        {/* Order badge */}
                                        <Badge variant="outline" className="text-xs">
                                            #{cat.order || index + 1}
                                        </Badge>

                                        {/* Actions */}
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() => handleEditCategory(cat)}
                                            >
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDeleteClick(cat)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}

                            {sortedCategories.length === 0 && (
                                <div className="text-center py-8 text-muted-foreground">
                                    Aucune catégorie. Créez-en une !
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={onClose}>
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit/Create Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nom de la catégorie</Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Ex: Boissons chaudes"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Icône</Label>
                            <div className="flex flex-wrap gap-2 p-3 bg-muted/30 rounded-lg max-h-32 overflow-y-auto">
                                {CATEGORY_EMOJIS.map(emoji => (
                                    <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => setForm({ ...form, icon: emoji })}
                                        className={cn(
                                            "w-10 h-10 text-xl rounded-lg flex items-center justify-center transition-all",
                                            form.icon === emoji 
                                                ? "bg-primary text-primary-foreground ring-2 ring-primary" 
                                                : "hover:bg-muted"
                                        )}
                                    >
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {editingCategory && (
                            <div className="space-y-2">
                                <Label>Position (ordre)</Label>
                                <Input
                                    type="number"
                                    value={form.order}
                                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                                    min="1"
                                />
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSaveCategory} className="bg-green-600 hover:bg-green-700">
                            {editingCategory ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-500" />
                            Supprimer "{deleteCategory?.name}" ?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-4">
                                {deleteCategory?.productCount > 0 ? (
                                    <>
                                        <p className="text-orange-600 font-medium">
                                            Cette catégorie contient {deleteCategory.productCount} produit(s).
                                        </p>
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    id="move"
                                                    name="deleteAction"
                                                    checked={deleteAction === 'move'}
                                                    onChange={() => setDeleteAction('move')}
                                                />
                                                <Label htmlFor="move" className="cursor-pointer">
                                                    Déplacer les produits vers une autre catégorie
                                                </Label>
                                            </div>
                                            {deleteAction === 'move' && (
                                                <Select value={moveToCategory} onValueChange={setMoveToCategory}>
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="Choisir une catégorie" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {categories
                                                            .filter(c => c.id !== deleteCategory?.id)
                                                            .map(cat => (
                                                                <SelectItem key={cat.id} value={cat.id}>
                                                                    {cat.icon} {cat.name}
                                                                </SelectItem>
                                                            ))}
                                                    </SelectContent>
                                                </Select>
                                            )}

                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    id="delete"
                                                    name="deleteAction"
                                                    checked={deleteAction === 'delete'}
                                                    onChange={() => setDeleteAction('delete')}
                                                />
                                                <Label htmlFor="delete" className="cursor-pointer text-red-600">
                                                    Supprimer aussi les produits (irréversible)
                                                </Label>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <p>Cette catégorie est vide et peut être supprimée.</p>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDelete}
                            className="bg-red-600 hover:bg-red-700"
                            disabled={deleteCategory?.productCount > 0 && deleteAction === 'move' && !moveToCategory}
                        >
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

export default CategoryManager;
